import React, { forwardRef } from 'react';

// Lazily load lucide-react at runtime to avoid module init order / TDZ
let _lucideCache: Record<string, any> | null = null;
function ensureLucide(): Promise<Record<string, any>> {
  if (_lucideCache) return Promise.resolve(_lucideCache);
  return import('lucide-react').then((mod) => {
    _lucideCache = mod as Record<string, any>;
    return _lucideCache;
  });
}

function makeExportedIcon(name: string) {
  const Component = forwardRef<any, any>(function Icon(props, ref) {
    const [C, setC] = React.useState<any>(null);
    React.useEffect(() => {
      let mounted = true;
      ensureLucide().then((mod) => {
        if (!mounted) return;
        const lucideName = name === 'ImageIcon' ? 'Image' : name;
        setC(() => (mod as any)[lucideName] || null);
      }).catch(() => {
        /* ignore load errors for now */
      });
      return () => { mounted = false; };
    }, []);

    if (!C) return null;
    return <C ref={ref} {...props} />;
  });
  Component.displayName = `Icon(${name})`;
  return Component;
}

// Export individually so existing imports keep working.
// Create explicit named exports so Rollup can statically analyze them.
export const Store = makeExportedIcon('Store');
export const Newspaper = makeExportedIcon('Newspaper');
export const Check = makeExportedIcon('Check');
export const Search = makeExportedIcon('Search');
export const Mail = makeExportedIcon('Mail');
export const MailOpen = makeExportedIcon('MailOpen');
export const User = makeExportedIcon('User');
export const Users = makeExportedIcon('Users');
export const Settings = makeExportedIcon('Settings');
export const MapPin = makeExportedIcon('MapPin');
export const FileText = makeExportedIcon('FileText');
export const Printer = makeExportedIcon('Printer');
export const Folder = makeExportedIcon('Folder');
export const ListTodo = makeExportedIcon('ListTodo');
export const Brush = makeExportedIcon('Brush');
export const LogOut = makeExportedIcon('LogOut');
export const Loader2 = makeExportedIcon('Loader2');
export const PlusCircle = makeExportedIcon('PlusCircle');
export const Package = makeExportedIcon('Package');
export const Globe = makeExportedIcon('Globe');
export const Tag = makeExportedIcon('Tag');
export const Image = makeExportedIcon('ImageIcon');
export const Book = makeExportedIcon('Book');
export const Smartphone = makeExportedIcon('Smartphone');
export const Truck = makeExportedIcon('Truck');
export const Tree = makeExportedIcon('Tree');
export const ChevronDown = makeExportedIcon('ChevronDown');
export const ChevronLeft = makeExportedIcon('ChevronLeft');
export const ChevronRight = makeExportedIcon('ChevronRight');
export const Circle = makeExportedIcon('Circle');
export const BookOpen = makeExportedIcon('BookOpen');
export const Shirt = makeExportedIcon('Shirt');
export const Grid = makeExportedIcon('Grid');
export const Home = makeExportedIcon('Home');
export const Film = makeExportedIcon('Film');
export const DollarSign = makeExportedIcon('DollarSign');
export const Repeat = makeExportedIcon('Repeat');
export const Gavel = makeExportedIcon('Gavel');
export const Clock = makeExportedIcon('Clock');
export const Pencil = makeExportedIcon('Pencil');
export const Car = makeExportedIcon('Car');
export const HardDrive = makeExportedIcon('HardDrive');
export const Info = makeExportedIcon('Info');
export const Beaker = makeExportedIcon('Beaker');
export const LogIn = makeExportedIcon('LogIn');
export const Paintbrush = makeExportedIcon('Paintbrush');
export const Bell = makeExportedIcon('Bell');
export const CheckCircle = makeExportedIcon('CheckCircle');
export const Camera = makeExportedIcon('Camera');
export const Trash2 = makeExportedIcon('Trash2');
export const Reply = makeExportedIcon('Reply');
export const AlertTriangle = makeExportedIcon('AlertTriangle');
export const Download = makeExportedIcon('Download');
export const ExternalLink = makeExportedIcon('ExternalLink');
export const Shield = makeExportedIcon('Shield');
export const Smile = makeExportedIcon('Smile');
export const GripVertical = makeExportedIcon('GripVertical');
export const PanelLeft = makeExportedIcon('PanelLeft');
export const Bike = makeExportedIcon('Bike');
export const Activity = makeExportedIcon('Activity');
export const Shuffle = makeExportedIcon('Shuffle');
export const Motorbike = makeExportedIcon('Motorbike');
export const CreditCard = makeExportedIcon('CreditCard');
export const X: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 6l-12 12" />
    <path d="M6 6l12 12" />
  </svg>
);
export const CalendarDays = makeExportedIcon('CalendarDays');
export const UserPlus = makeExportedIcon('UserPlus');
export const UserMinus = makeExportedIcon('UserMinus');
export const MessageSquare = makeExportedIcon('MessageSquare');
export const QrCode = makeExportedIcon('QrCode');
export const MessagesSquare = makeExportedIcon('MessagesSquare');
export const Star = makeExportedIcon('Star');
export const GalleryHorizontal = makeExportedIcon('GalleryHorizontal');
export const RotateCcw = makeExportedIcon('RotateCcw');
export const ArrowLeft = makeExportedIcon('ArrowLeft');
export const ArrowRight = makeExportedIcon('ArrowRight');
export const Crop = makeExportedIcon('Crop');
export const ImageOff = makeExportedIcon('ImageOff');
export const Upload = makeExportedIcon('Upload');
export const Eye = makeExportedIcon('Eye');
export const CalendarIcon = makeExportedIcon('CalendarIcon');
export const FolderPlus = makeExportedIcon('FolderPlus');
export const FolderMinus = makeExportedIcon('FolderMinus');
export const MoreHorizontal = makeExportedIcon('MoreHorizontal');
export const MoreVertical = makeExportedIcon('MoreVertical');
export const Bookmark = makeExportedIcon('Bookmark');
export const ChevronUp = makeExportedIcon('ChevronUp');
export const Link = makeExportedIcon('Link');
export const Bold = makeExportedIcon('Bold');
export const Italic = makeExportedIcon('Italic');
export const List = makeExportedIcon('List');
export const ListOrdered = makeExportedIcon('ListOrdered');
export const Send = makeExportedIcon('Send');
export const ShoppingBag = makeExportedIcon('ShoppingBag');
export const Plus = makeExportedIcon('Plus');
export const LocateFixed = makeExportedIcon('LocateFixed');
export const Minus = makeExportedIcon('Minus');
export const ShoppingCart = makeExportedIcon('ShoppingBasket');
export const ReceiptText = makeExportedIcon('ReceiptText');
export const WalletCards = makeExportedIcon('WalletCards');
export const Heart = makeExportedIcon('Heart');
export const Headset = makeExportedIcon('Headset');
export const MessageSquareText = makeExportedIcon('MessageSquareText');
export const Menu = makeExportedIcon('Menu');
export const Heading1 = makeExportedIcon('Heading1');
export const Heading2 = makeExportedIcon('Heading2');
export const Edit3 = makeExportedIcon('Edit3');
export const Plane = makeExportedIcon('Plane');
export const CarTaxiFront = makeExportedIcon('CarTaxiFront');
export const Gift = makeExportedIcon('Gift');
export const Binoculars = makeExportedIcon('Binoculars');
export const Sun = makeExportedIcon('Sun');
export const Moon = makeExportedIcon('Moon');
export const SunMoon = makeExportedIcon('SunMoon');
export const Radio = makeExportedIcon('Radio');
export const WifiOff = makeExportedIcon('WifiOff');
export const AlertCircle = makeExportedIcon('AlertCircle');
export const TrendingUp = makeExportedIcon('TrendingUp');
export const CheckCircle2 = makeExportedIcon('CheckCircle2');
export const Crosshair = makeExportedIcon('Crosshair');
export const Rocket = makeExportedIcon('Rocket');
export const Briefcase = makeExportedIcon('Briefcase');
export const Zap = makeExportedIcon('Zap');
export const Lightbulb = makeExportedIcon('Lightbulb');
export const RotateCw = makeExportedIcon('RotateCw');
export const Volume2 = makeExportedIcon('Volume2');
export const VolumeX = makeExportedIcon('VolumeX');
export const SkipBack = makeExportedIcon('SkipBack');
export const SkipForward = makeExportedIcon('SkipForward');
export const Road = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <svg
    ref={ref}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 17v4"/>
    <path d="M12 5V3"/>
    <path d="M12 9v3"/>
    <path d="M2.077 18.449A2 2 0 0 0 4 21h16a2 2 0 0 0 1.924-2.55l-4-14A2 2 0 0 0 16 3H8a2 2 0 0 0-1.924 1.45z"/>
  </svg>
));
Road.displayName = 'Road';
export const Play = makeExportedIcon('Play');
export const Pause = makeExportedIcon('Pause');
export const Music = makeExportedIcon('Music');
export const SlidersHorizontal = makeExportedIcon('SlidersHorizontal');

const defaultExport: Record<string, any> = {
  Store,Newspaper,Check,Search,Mail,MailOpen,User,Users,Settings,MapPin,FileText,Printer,Folder,ListTodo,Brush,LogOut,Loader2,PlusCircle,Package,Globe,Tag,Image,Book,Smartphone,Truck,Tree,ChevronDown,ChevronLeft,ChevronRight,Circle,BookOpen,Shirt,Grid,Home,Film,DollarSign,Repeat,Gavel,Clock,Pencil,Car,HardDrive,Info,Beaker,LogIn,Paintbrush,Bell,CheckCircle,Camera,Trash2,AlertTriangle,Download,ExternalLink,GripVertical,X,CalendarDays,UserPlus,UserMinus,MessageSquare,MessagesSquare,Star,GalleryHorizontal,RotateCcw,ArrowLeft,ArrowRight,Crop,ImageOff,Upload,Eye,CalendarIcon,FolderPlus,FolderMinus,MoreHorizontal,MoreVertical,Bookmark,ChevronUp,Link,Bold,Italic,List,ListOrdered,Send,ShoppingBag,CreditCard,Plus,LocateFixed,Minus,ShoppingCart,ReceiptText,WalletCards,Heart,Headset,MessageSquareText,Menu,Heading1,Heading2,Edit3,Sun,Moon,Bike,Activity,Shuffle,Motorbike,Radio,WifiOff,AlertCircle,TrendingUp,CheckCircle2,Crosshair,Rocket,Briefcase,Zap,Lightbulb,RotateCw,Volume2,VolumeX,SkipBack,SkipForward,Road,Play,Pause,Music,SlidersHorizontal
};

export default defaultExport;
